import { Component, OnInit, signal, computed } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { State } from '../../../../core/store';
import { Store } from '@ngrx/store';
import { DateRangeModel } from '../../../../models/dateRangeModel';
import { PagingResultsModel } from '../../../../models/pagingResultsModel';
import { EmployeeCensusModel } from '../../../../models/userProfileModel';
import { DropdownFilterService } from '../../../../core/services/dropdown-filter.service';
import { exportEmployeeCensusListToExcel, exportEmployeeCensusListToExcelSuccess, getEmployeeCensusList, loadEmployeeUsers, loadViewableCampuses } from '../../../../core/store/employee/employee.action';
import { deepClone, downloadFile } from '../../../../helpers/utils';
import { Actions, ofType } from '@ngrx/effects';
import { tap } from 'rxjs';
import { Router } from '@angular/router';
import { UserAutoCompleteItem } from '../../../../models/utilModels';

@Component({
    selector: 'app-report-census',
    templateUrl: './report-census.component.html',
    styleUrls: ['./report-census.component.scss'],
    standalone: false
})
export class ReportCensusComponent extends BaseComponent implements OnInit {
  list: PagingResultsModel<EmployeeCensusModel>;
  filter: any;
  canEditUser: boolean = false;
  canViewEmployeeExclusionReports: boolean = false;

  loadingCensusList: boolean = true;
  loadingCensusExcel: boolean = false;

  campusList: SelectItem[];
  campusId: number = 0;

  selectedUserEmployee: UserAutoCompleteItem;
  userEmployeeList: UserAutoCompleteItem[];
  userId: number = 0;

  employeePayrollNumber: string = "";

  birthdate: string = null;

  showTerminatedEmployees: boolean = false;
  showInactiveUsers: boolean = false;

  totalRecords = signal<number>(0);
  dropdownOptions = computed(() => {
    var total = this.totalRecords();
    return [
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
      { label: '200', value: 200 },
      { label: '500', value: 500 },
      { label: 'All', value: total > 0 ? total : 20 } 
    ];
  });

  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store<State>) {
    super();
  }

  ngOnInit() {
    this.subs.push(
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canEditUser = permissionIDs && !!permissionIDs.find(p => p === 6001 || p === 6002); //HR Manager, HR Super Admin
        this.canViewEmployeeExclusionReports = permissionIDs && !!permissionIDs.find(p => p === 3310); //View employee exclusion reports
      }),
      this.store.select(s => s.employeeState.loadingCensusList).subscribe(loadingCensusList => {
        this.loadingCensusList = loadingCensusList;
      }),
      this.store.select(s => s.employeeState.loadingCensusExcel).subscribe(loadingCensusExcel => {
        this.loadingCensusExcel = loadingCensusExcel;
      }),
      this.store.select(s => s.employeeState.campuses).subscribe(campuses => {
        this.campusList = campuses && campuses.length > 0 ? campuses
          : [{ label: 'loading...', value: undefined }];
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userEmployeeList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.store.select(s => s.employeeState.censusList).subscribe(censusList => {
        this.list = censusList;
        if(censusList != null)
     	    this.totalRecords.set(censusList.totalRecords);
      }),
      this.actions$.pipe(
        ofType(exportEmployeeCensusListToExcelSuccess),
        tap(action => {
          downloadFile(action.doc, 'EmployeeCensus.xlsx');
        })
      ).subscribe()
    );

    this.store.dispatch(loadViewableCampuses());
  }

  reloadList(event) {
    this.filter = event;
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.employeePayrollNumber = { Name: 'employeePayrollNumber', Value: { value: this.employeePayrollNumber } };
    this.filter.filters.birthdate = { Name: 'birthdate', Value: { value: this.birthdate } };
    this.filter.filters.showTerminatedEmployees = { Name: 'showTerminatedEmployees', Value: { value: this.showTerminatedEmployees.toString() } };
    this.filter.filters.showInactiveUsers = { Name: 'showInactiveUsers', Value: { value: this.showInactiveUsers.toString() } };

    this.store.dispatch(getEmployeeCensusList({
      filter: this.filter
    }));
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.campusId,
      payrollClassificationId: null,
      showInactive: this.showInactiveUsers,
      showTerminated: this.showTerminatedEmployees
    }));
  }

  reloadListAfterStaffChage(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.userId = selectedUser.value.id;
    this.reloadList(this.filter);
  }

  reloadListAfterClear(event) {
    
    this.userId = 0;
    this.reloadList(this.filter);
  }

  exportToExcel() {
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.employeePayrollNumber = { Name: 'employeePayrollNumber', Value: { value: this.employeePayrollNumber } };
    this.filter.filters.birthdate = { Name: 'birthdate', Value: { value: this.birthdate } };
    this.filter.filters.showTerminatedEmployees = { Name: 'showTerminatedEmployees', Value: { value: this.showTerminatedEmployees.toString() } };
    this.filter.filters.showInactiveUsers = { Name: 'showInactiveUsers', Value: { value: this.showInactiveUsers.toString() } };

    this.store.dispatch(exportEmployeeCensusListToExcel({
      filter: this.filter
    }));
  }

  editEmployee(employee: EmployeeCensusModel = null) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/employee-file/detail/' + employee.userId])
    );

    window.open(url, '_blank');

    //this.router.navigate(['/employee-file/detail/' + employee.userId]);
  }
}
